import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Copy, Phone, Send, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import SupportTickets from '@/components/SupportTickets';

const ADMIN_TELEGRAM_ID = 'https://t.me/Amlak3_ir';
const TELEGRAM_CHANNEL = 'https://t.me/+nKI3FXpnvtZkNjk0';
const ADMIN_PHONE = '+989058139626';

const copy = async (text: string, label: string) => {
    try {
        await navigator.clipboard.writeText(text);
        toast.success(`${label} کپی شد`);
    } catch {
        toast.error('امکان کپی وجود ندارد');
    }
};

const Support: React.FC = () => {
    return (
        <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 p-4 md:p-6" dir="rtl">
            <div className="max-w-3xl mx-auto space-y-6">
                <div className="text-right space-y-2">
                    <h1 className="text-2xl md:text-3xl font-bold text-green-900">پشتیبانی نرم‌افزار های لایسنس اپ</h1>
                    <p className="text-green-700">برای ارتباط با تیم پشتیبانی از یکی از راه‌های زیر استفاده کنید</p>
                </div>

                <Alert className="border-green-200 bg-white/70 backdrop-blur">
                    <AlertDescription>
                        🕓 ساعات پاسخگویی: همه‌روزه از ساعت <b>۹ صبح تا ۱۸ عصر</b> (به‌جز تعطیلات رسمی)
                    </AlertDescription>
                </Alert>

                {/* تیکت‌ها — راه اصلی پیگیری، چون سابقه‌اش می‌ماند و قابل ارجاع است */}
                <SupportTickets />

                <div className="flex items-center gap-3 pt-2">
                    <span className="h-px flex-1 bg-green-200" />
                    <span className="text-sm text-green-700">راه‌های تماس مستقیم</span>
                    <span className="h-px flex-1 bg-green-200" />
                </div>

                {/* ادمین تلگرام */}
                <Card className="border-green-200 shadow-sm">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-green-900">
                            <Send className="h-5 w-5" /> ارتباط با ادمین تلگرام
                        </CardTitle>
                        <CardDescription className="text-green-700">
                            برای پشتیبانی سریع، از طریق آی‌دی زیر در تلگرام پیام بدهید
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                        <a
                            href={ADMIN_TELEGRAM_ID}
                            target="_blank"
                            rel="noreferrer"
                            className="text-green-800 underline break-all"
                        >
                            {ADMIN_TELEGRAM_ID}
                        </a>
                        <div className="flex gap-2 w-full sm:w-auto">
                            <Button
                                variant="outline"
                                className="border-green-300 text-green-700 hover:bg-green-50 flex-1 sm:flex-none"
                                onClick={() => window.open(ADMIN_TELEGRAM_ID, '_blank')}
                            >
                                <Link2 className="ml-2 h-4 w-4" />
                                باز کردن
                            </Button>
                            <Button
                                variant="outline"
                                className="border-green-300 text-green-700 hover:bg-green-50"
                                onClick={() => copy(ADMIN_TELEGRAM_ID, 'آی‌دی تلگرام')}
                            >
                                <Copy className="ml-2 h-4 w-4" />
                                کپی
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* کانال تلگرام */}
                <Card className="border-green-200 shadow-sm">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-green-900">
                            <Send className="h-5 w-5" /> کانال اطلاع‌رسانی لایسنس اپ
                        </CardTitle>
                        <CardDescription className="text-green-700">
                            جدیدترین اخبار، بروزرسانی‌ها و آموزش‌های نرم‌افزار
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                        <a
                            href={TELEGRAM_CHANNEL}
                            target="_blank"
                            rel="noreferrer"
                            className="text-green-800 underline break-all"
                        >
                            {TELEGRAM_CHANNEL}
                        </a>
                        <div className="flex gap-2 w-full sm:w-auto">
                            <Button
                                variant="outline"
                                className="border-green-300 text-green-700 hover:bg-green-50 flex-1 sm:flex-none"
                                onClick={() => window.open(TELEGRAM_CHANNEL, '_blank')}
                            >
                                <Link2 className="ml-2 h-4 w-4" />
                                باز کردن
                            </Button>
                            <Button
                                variant="outline"
                                className="border-green-300 text-green-700 hover:bg-green-50"
                                onClick={() => copy(TELEGRAM_CHANNEL, 'آدرس کانال')}
                            >
                                <Copy className="ml-2 h-4 w-4" />
                                کپی
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* شماره تماس */}
                <Card className="border-green-200 shadow-sm">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-green-900">
                            <Phone className="h-5 w-5" /> شماره تماس پشتیبانی
                        </CardTitle>
                        <CardDescription className="text-green-700">
                            تماس تلفنی در ساعات اداری
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                        <a href={`tel:${ADMIN_PHONE}`} dir="ltr" className="text-green-800 underline">
                            {ADMIN_PHONE}
                        </a>
                        <div className="flex gap-2 w-full sm:w-auto">
                            <Button
                                variant="outline"
                                className="border-green-300 text-green-700 hover:bg-green-50 flex-1 sm:flex-none"
                                onClick={() => (window.location.href = `tel:${ADMIN_PHONE}`)}
                            >
                                <Phone className="ml-2 h-4 w-4" />
                                تماس
                            </Button>
                            <Button
                                variant="outline"
                                className="border-green-300 text-green-700 hover:bg-green-50"
                                onClick={() => copy(ADMIN_PHONE, 'شماره تماس')}
                            >
                                <Copy className="ml-2 h-4 w-4" />
                                کپی
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default Support;
